export const SUPPORTED_LANGUAGES = ["en", "it", "fr", "es"];

// Per-section save rules (keyed by basePath).
// Empty / missing entry => "at least one language fully filled".
// Non-empty list => every listed language must be fully filled.
const SECTION_MANDATORY_LANGUAGES = {
  "cyber-tips-cdp": ["en", "it", "fr"],
};

// Short labels for dropdowns, field labels, and warning text.
export const LANGUAGE_LABELS = {
  en: "English",
  es: "Spanish",
  fr: "French",
  it: "Italian",
};

// Longer labels used by language chips on list tables.
export const LANGUAGE_CHIP_LABELS = {
  en: "ENGLISH (EN-gb)",
  es: "SPANISH (ES-es)",
  fr: "FRENCH (FR-fr)",
  it: "ITALIAN (IT-it)",
};

// Chip colors for fill state in prompt/tag editors.
export const COMPLETION_STATUS_STYLES = {
  active: { backgroundColor: "#ece2ff", color: "#5f35b5" },
  complete: { backgroundColor: "#daf5d7", color: "#1d6b3b" },
  empty: { backgroundColor: "#f3f3f3", color: "#666" },
  partial: { backgroundColor: "#fbe9c8", color: "#8a5600" },
};

// Maps UI language code -> news form/API text field name.
export const NEWS_TEXT_FIELD_BY_LANGUAGE = {
  en: "text_en",
  es: "text_es",
  fr: "text_fr",
  it: "text_default",
};

// Mandatory languages for a prompt/tag section, or [] when none.
export const getMandatoryLanguagesForPromptSection = (basePath) =>
  SECTION_MANDATORY_LANGUAGES[basePath] || [];

// True when the section requires every mandatory language (e.g. cyber tips).
export const requiresAllMandatoryLanguages = (basePath) =>
  getMandatoryLanguagesForPromptSection(basePath).length > 0;

// Languages shown/editable for a section: company languages + any mandatory ones.
export const getEditableLanguagesForPromptSection = (
  basePath,
  validLanguages = []
) => {
  const combined = new Set([
    ...(validLanguages || []),
    ...getMandatoryLanguagesForPromptSection(basePath),
  ]);

  const languages = SUPPORTED_LANGUAGES.filter((language) =>
    combined.has(language)
  );

  return languages.length ? languages : ["en", "it"];
};

export const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

// Read a multilingual field for one language only (no fallback to another lang).
// Accepts a plain string or { en, it, fr, es }.
export const getLocalizedField = (value, language) => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object") {
    return value[language] || "";
  }

  return "";
};

// Save-ready for prompts: title + description + prompttext all filled for language.
export const isPromptLanguageCompleteForSave = (values, language) =>
  isNonEmptyString(getLocalizedField(values?.title, language)) &&
  isNonEmptyString(getLocalizedField(values?.description, language)) &&
  isNonEmptyString(getLocalizedField(values?.prompttext, language));

// Save-ready for tags: name filled for language.
export const isTagNameCompleteForSave = (tagname, language) =>
  isNonEmptyString(getLocalizedField(tagname, language));

// List/table filter: prompt has at least some content in language (not full save-complete).
export const hasPromptContentInLanguage = (prompt, language) =>
  ["title", "description", "prompttext"].some((field) =>
    isNonEmptyString(getLocalizedField(prompt?.[field], language))
  );

// Formats codes into a human list, e.g. ["en","fr"] -> "English, French".
export const formatLanguageList = (languages = []) =>
  languages
    .map((language) => LANGUAGE_LABELS[language] || language.toUpperCase())
    .join(", ");

// Languages from the list that fail isComplete(language).
export const getIncompleteLanguages = (languages = [], isComplete) =>
  languages.filter((language) => !isComplete(language));

// Styles for a completion-state chip (complete / active / partial / empty).
export const getCompletionStatusStyles = (state) =>
  COMPLETION_STATUS_STYLES[state] || COMPLETION_STATUS_STYLES.empty;

// Binary table chip styles (selected vs not), reused from completion palette.
export const getLanguageChipStyles = (isActive) =>
  getCompletionStatusStyles(isActive ? "active" : "empty");

// All editable languages except the pinned/preferred one (for side chips).
export const getSecondaryLanguages = (validLanguages, preferredLanguage) =>
  validLanguages.filter((language) => language !== preferredLanguage);

// Normalize locale-ish values ("it-IT", "FR") to a supported short code, or null.
export const normalizeLanguageCode = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const shortCode = normalized.split(/[-_]/)[0];

  return SUPPORTED_LANGUAGES.includes(shortCode) ? shortCode : null;
};

// Company languages from /info/my, normalized and deduped (default en/it).
export const getValidLanguagesFromUser = (user) => {
  if (!Array.isArray(user?.validLanguages) || !user.validLanguages.length) {
    return ["en", "it"];
  }

  const languages = user.validLanguages
    .map(normalizeLanguageCode)
    .filter(Boolean)
    .filter((language, index, values) => values.indexOf(language) === index);

  return languages.length ? languages : ["en", "it"];
};

// User preferred/pinned language; otherwise first valid language.
export const getPreferredLanguageFromUser = (user, validLanguages) => {
  const preferred = normalizeLanguageCode(user?.preferredLanguage);

  if (preferred && validLanguages.includes(preferred)) {
    return preferred;
  }

  return validLanguages[0];
};

// News form field name for a language (falls back to Italian text_default).
export const getNewsFieldByLanguage = (language) =>
  NEWS_TEXT_FIELD_BY_LANGUAGE[language] || "text_default";

// True when a content block has usable data for a language.
export const hasNewsBlockLanguageContent = (block, language) => {
  const value = block?.data?.[language];

  if (block?.type === "markdown") {
    return isNonEmptyString(value);
  }

  if (block?.type === "image") {
    return Boolean(value);
  }

  if (block?.type === "attachment") {
    return Boolean(value?.url);
  }

  return false;
};

// Save-ready for news in at least one language: title + subtitle + at least one content block.
export const isNewsLanguageCompleteForSave = (values, language) => {
  const fieldName = getNewsFieldByLanguage(language);
  const text = values?.[fieldName];

  return (
    isNonEmptyString(text?.title) &&
    isNonEmptyString(text?.subtitle) &&
    (values?.content || []).some((block) =>
      hasNewsBlockLanguageContent(block, language)
    )
  );
};

// List/table filter: title is enough
export const hasNewsContentInLanguage = (news, language) => {
  const fieldName = getNewsFieldByLanguage(language);
  return isNonEmptyString(news?.[fieldName]?.title);
};

// Ensure a news text_* field has expected keys.
export const ensureNewsTextBlob = (value) => ({
  keyPoints: Array.isArray(value?.keyPoints) ? value.keyPoints : [],
  subtitle: value?.subtitle || "",
  text: value?.text || "",
  title: value?.title || "",
});

// Ensure a full { en, it, fr, es } object with "" for missing keys (form/API shape).
export const ensurePromptMultilingualObject = (value) => {
  const result = {};

  SUPPORTED_LANGUAGES.forEach((language) => {
    result[language] = value?.[language] || "";
  });

  return result;
};

// Map filled-field count + active chip into a completion state key for styling.
export const getCompletionState = ({
  completedLanguage,
  isActive,
  totalRequiredFields,
}) => {
  if (completedLanguage >= totalRequiredFields) {
    return "complete";
  }

  if (isActive) {
    return "active";
  }

  if (completedLanguage > 0) {
    return "partial";
  }

  return "empty";
};
